import type { ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { ApiError } from '@/lib/api'
import { Icon, type IconName } from './Icon'

/**
 * Состояние блока словами (макет CompStates): нет данных / нет доступа / не настроено /
 * ошибка.
 *
 * Четыре состояния — четыре разных утверждения, и путать их нельзя. Пустота значит «у вас
 * этого нет», запрет — «вам это не положено», «не настроено» — «система ещё не знает, что
 * считать», ошибка — «спросили, но не получили». Нарисованные одной пустой таблицей, они
 * сливаются в «данных нет», и каждый из трёх последних случаев остаётся невидимым.
 */
export type BlockStateKind = 'empty' | 'forbidden' | 'setup' | 'error'

const KIND_CLASS: Record<BlockStateKind, string> = {
  empty: 'state',
  forbidden: 'state is-forbidden',
  setup: 'state is-setup',
  error: 'state is-error',
}

const KIND_ICON: Record<BlockStateKind, IconName> = {
  empty: 'inbox',
  forbidden: 'lock',
  setup: 'settings',
  error: 'alert',
}

export function BlockState({
  kind,
  title,
  description,
  actions,
  code,
  compact = false,
}: {
  kind: BlockStateKind
  title: string
  description?: ReactNode
  actions?: ReactNode
  /** Код ответа и прочие подробности для поддержки — моноширинно, мелко. */
  code?: string
  /** Внутри панели — без крупной иконки и с меньшими отступами. */
  compact?: boolean
}) {
  const { t } = useApp()
  return (
    <div
      className={KIND_CLASS[kind]}
      role={kind === 'error' ? 'alert' : undefined}
      style={compact ? { padding: '24px 16px' } : undefined}
    >
      <span className="state-kind">{t(`state.kind.${kind}`)}</span>
      {!compact && (
        <span className="state-ico">
          <Icon name={KIND_ICON[kind]} />
        </span>
      )}
      <span className="st" style={compact ? { fontSize: 14 } : undefined}>{title}</span>
      {description && <span className="sd">{description}</span>}
      {actions && <span className="sa">{actions}</span>}
      {code && <span className="code">{code}</span>}
    </div>
  )
}

/**
 * Ошибка запроса как состояние блока.
 *
 * 403 — это запрет, а не поломка, и говорится словами запрета: «повторить» на отказ по
 * правам отправляет человека жать кнопку, которая не поможет.
 */
export function QueryErrorState({
  error,
  title,
  onRetry,
}: {
  error: unknown
  title: string
  onRetry: () => void
}) {
  const { t } = useApp()
  if (error instanceof ApiError && (error.isForbidden || error.code === 'report.forbidden_source')) {
    return <BlockState kind="forbidden" compact title={t('state.forbidden.title')} description={t('state.forbidden.text')} />
  }
  return (
    <BlockState
      kind="error"
      compact
      title={title}
      description={t('state.error.text')}
      actions={
        <button className="btn btn-secondary btn-sm" type="button" onClick={onRetry}>
          {t('state.retry')}
        </button>
      }
      code={error instanceof ApiError ? `${error.status}${error.code ? ` · ${error.code}` : ''}` : undefined}
    />
  )
}
